import React, { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import OrderAPI from 'api/orbitV1/OrderAPI'
import ColorBox from 'common/Box/ColorWhiteBox'
import { ColorBoxHeader } from 'common/ui/colorBox'
import { ColorBoxContent } from 'common/ui/colorBox'
import Pagination from 'common/ui/Pagination'
import { SearchBarBox, SearchBarInput } from 'common/ui/searchBar'
import format from 'date-fns/format'
import OrbitSession from 'hooks/useCookies'
import { OrderGetDataBase } from 'types/api/v1/order.type'

import ViewOrderDrawer from './components/ViewOrderDrawer'

export default function ViewOrders() {
  const navigate = useNavigate()
  const [searchOrder, setSearchOrder] = useState('')
  const [viewOrder, setSelectedOrderView] = useState<OrderGetDataBase>()
  const deferredSearchInput = useDeferredValue(searchOrder)

  const ORDER_LIMIT = 150
  const [orders, setOrders] = useState<OrderGetDataBase[]>([])
  const [drawerOpen, setDrawerOpen] = useState(false)
  const orgId = OrbitSession.get('orgId')

  const filteredList = useMemo(() => {
    return orders.filter((order) => order.orderNo.includes(deferredSearchInput))
  }, [deferredSearchInput])

  const {
    status,
    data: orderStatus,
    error
  } = useQuery(['getClientOrders'], async () => {
    const { data } = await OrderAPI.getClientOrdersByOrgId(orgId, ORDER_LIMIT)
    return data
  })

  if (error) {
    toast.error('Error loading data')
  }
  useEffect(() => {
    if (status === 'success') {
      setOrders(
        orderStatus.filter((orgOrder) => {
          return orgOrder.orgId === orgId
        })
      )
    }
  }, [orderStatus])

  const handleViewOrder = (selectedViewOrder) => {
    setDrawerOpen(true)
    setSelectedOrderView(selectedViewOrder)
  }

  const handleClose = () => {
    setDrawerOpen(false)
  }

  return (
    <>
      <ColorBoxHeader>View Orders</ColorBoxHeader>
      <Box sx={{ marginTop: '24px' }}>
        <ColorBoxContent>
          <ColorBox>
            <Box
              sx={{
                display: 'flex',
                direction: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
              }}
            >
              <SearchBarBox>
                <SearchBarInput
                  onChange={(e) => setSearchOrder(e.target.value)}
                  disableUnderline
                  placeholder='Search by Order ID'
                />
              </SearchBarBox>
            </Box>
            <Box>
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      'Creation Date',
                      'Order ID',
                      'No. of SIMs',
                      'Total Charges',
                      'Status',
                      'View'
                    ].map((header, index) => {
                      return (
                        <TableCell
                          sx={{ fontWeight: '600', fontSize: '16px' }}
                          align='left'
                          key={`${header}-${index}`}
                        >
                          {header}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {useMemo(
                    () =>
                      (!deferredSearchInput ? orders : filteredList).map(
                        (order, index) => (
                          <TableRow key={`${order._id}-${index}`}>
                            <TableCell>
                              {format(
                                new Date(order.createdAt),
                                'dd MMMM yyyy'
                              )}
                            </TableCell>

                            <TableCell>{order.orderNo}</TableCell>
                            <TableCell>{order.items[0].quantity}</TableCell>
                            <TableCell>${order.price.toFixed(2)}</TableCell>
                            <TableCell>
                              {order.status === 'BOOK DELIVERY' ? (
                                <Tooltip title='Book Now' placement='top'>
                                  <Button
                                    variant='noBackground'
                                    onClick={() =>
                                      navigate(
                                        `/app/book-delivery/${order.orderNo}`
                                      )
                                    }
                                  >
                                    {order.status}
                                  </Button>
                                </Tooltip>
                              ) : (
                                <>{order.status}</>
                              )}
                            </TableCell>
                            <TableCell>
                              <IconButton
                                onClick={() => handleViewOrder(order)}
                              >
                                <SearchIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )
                      ),
                    [orders, filteredList]
                  )}
                </TableBody>
              </Table>
              {viewOrder && (
                <ViewOrderDrawer
                  drawerOpen={drawerOpen}
                  handleClose={handleClose}
                  selectedOrder={viewOrder}
                />
              )}

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginTop: '16px'
                }}
              >
                <Pagination items={orders.length} limit={ORDER_LIMIT} />
              </Box>
            </Box>
          </ColorBox>
        </ColorBoxContent>
      </Box>
    </>
  )
}
